import { useEffect, useState } from 'react';
import { collection, onSnapshot, doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { Box, Typography, Button, Paper, Chip, IconButton, Stack, Checkbox } from '@mui/material';
import { Add, Edit, Delete } from '@mui/icons-material';
import { db } from './firebase';
import { useAuth } from './AuthContext';
import { fmtDate, daysDiff } from './helpers';
import TaskModal from './TaskModal';

export default function Task() {
  const { staff } = useAuth();
  const [items, setItems] = useState([]);
  const [contatti, setContatti] = useState([]);
  const [filter, setFilter] = useState('aperti');
  const [modal, setModal] = useState(null);

  const isAdmin = staff?.role === 'admin';

  useEffect(() => {
    const u1 = onSnapshot(collection(db, 'crm_task'), s => setItems(s.docs.map(d => ({ id: d.id, ...d.data() }))));
    const u2 = onSnapshot(collection(db, 'crm_contatti'), s => setContatti(s.docs.map(d => ({ id: d.id, ...d.data() }))));
    return () => { u1(); u2(); };
  }, []);

  // Visibilità: pubblici visibili a tutti, privati solo all'owner
const visibleItems = items.filter(t => {
  if (!t.private) return true;
  return t.owner === staff?.nome;
});

  const filtered = visibleItems.filter(t => {
    if (filter === 'aperti') return !t.fatto;
    if (filter === 'fatti') return t.fatto;
    if (filter === 'miei') return !t.fatto && t.owner === staff?.nome;
    return true;
  }).sort((a, b) => {
    if (a.fatto !== b.fatto) return a.fatto ? 1 : -1;
    return (a.scadenza || '').localeCompare(b.scadenza || '');
  });

  const toggle = (t) => updateDoc(doc(db, 'crm_task', t.id), { fatto: !t.fatto });

  // Filtri visibili: admin vede tutti, sales vede solo "aperti / fatti / tutti" (no "miei" perché lei vede solo i suoi)
  const filterOptions = [
  { id: 'aperti', l: 'Aperti' },
  { id: 'miei', l: 'Miei (' + staff?.nome + ')' },
  { id: 'fatti', l: 'Completati' },
  { id: 'tutti', l: 'Tutti' }
];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>Task</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={() => setModal({ scadenza: new Date().toISOString().slice(0, 10), priority: 'media', owner: staff?.nome })}>Nuovo</Button>
      </Box>

      <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
        {filterOptions.map(o => (
          <Chip key={o.id} label={o.l} onClick={() => setFilter(o.id)} color={filter === o.id ? 'primary' : 'default'} variant={filter === o.id ? 'filled' : 'outlined'} />
        ))}
      </Stack>

      <Paper>
        {filtered.length === 0 ? <Box sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>Nessun task.</Box> : filtered.map(t => {
          const c = contatti.find(x => x.id === t.contattoId);
          const diff = daysDiff(t.scadenza);
          const overdue = !t.fatto && diff !== null && diff < 0;
          const today = !t.fatto && diff === 0;
          return (
            <Box key={t.id} sx={{ p: 1.5, borderBottom: 1, borderColor: 'divider', display: 'flex', alignItems: 'flex-start', gap: 1, '&:last-child': { borderBottom: 0 } }}>
              <Checkbox checked={!!t.fatto} onChange={() => toggle(t)} />
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" sx={{ textDecoration: t.fatto ? 'line-through' : 'none', color: t.fatto ? 'text.disabled' : 'text.primary' }}>{t.titolo}</Typography>
                <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
                  {t.scadenza && <Chip size="small" label={overdue ? Math.abs(diff) + 'g ritardo' : today ? 'Oggi' : fmtDate(t.scadenza)} color={overdue ? 'error' : today ? 'warning' : 'default'} />}
                  <Chip size="small" label={t.priority || 'media'} />
                  <Typography variant="caption" color="text.secondary">{t.owner}</Typography>
                  {c && <Typography variant="caption" color="primary">{c.nome}</Typography>}
                </Stack>
              </Box>
              <IconButton size="small" onClick={() => setModal(t)}><Edit fontSize="small" /></IconButton>
              <IconButton size="small" onClick={() => window.confirm('Eliminare?') && deleteDoc(doc(db, 'crm_task', t.id))}><Delete fontSize="small" /></IconButton>
            </Box>
          );
        })}
      </Paper>

      {modal && <TaskModal initial={modal} onClose={() => setModal(null)} />}
    </Box>
  );
}